// Remember a confirmed column mapping so the SAME sheet layout does not have to
// be re-mapped from scratch (§3.7 depth gap vs SAP Migration Cockpit / Odoo).
//
// The §3.7 hour uploads four to six files, and the fix-and-re-upload loop means
// the same 20-column products sheet gets mapped again and again. This keeps the
// last confirmed mapping per (kind × exact header set) and offers it back.
//
// Deliberately CLIENT-SIDE and deliberately an OFFER, never an auto-apply:
//  * client-side because the contract has no saved-mapping resource and Phase 3
//    changes no contracts — a tenant-level saved mapping is the right home for
//    it later (tracked as a module follow-up), and this shape ports cleanly;
//  * an offer because silently re-applying a mapping the user cannot see would
//    be exactly the quiet data loss the mapping step exists to prevent. The
//    proposal is applied only when the user presses the button, and every column
//    stays visible and editable afterwards.
//
// localStorage is per browser, so this is a convenience for one person on one
// machine and is never treated as authority: the server's `column_mapping` is.

import type { ImportKind } from "./import-model";

const PREFIX = "assessly.onboarding.import-mapping";
/** Guard against an unbounded key set on a shared machine. */
const MAX_ENTRIES = 40;

/**
 * A stable identity for "a sheet shaped like this": the kind plus its header set,
 * order-independent (a user who moves a column has the same sheet) and
 * case-insensitive on surrounding whitespace only.
 */
export function headerFingerprint(kind: ImportKind, headers: readonly string[]): string {
  const normalized = [...new Set(headers.map((h) => h.trim()))].sort().join("");
  return `${kind} ${normalized}`;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Private mode / disabled storage: the feature simply does not exist then.
    return null;
  }
}

function keyOf(fingerprint: string): string {
  // The fingerprint can be long; hashing keeps keys bounded and readable.
  let hash = 5381;
  for (let i = 0; i < fingerprint.length; i += 1) {
    hash = ((hash << 5) + hash + fingerprint.charCodeAt(i)) | 0;
  }
  return `${PREFIX}.${(hash >>> 0).toString(36)}`;
}

/** Store the mapping the user just confirmed for this sheet shape. */
export function rememberMapping(
  kind: ImportKind,
  headers: readonly string[],
  mapping: Record<string, string>,
): void {
  const store = storage();
  if (store === null || Object.keys(mapping).length === 0) return;
  try {
    const fingerprint = headerFingerprint(kind, headers);
    store.setItem(keyOf(fingerprint), JSON.stringify({ fingerprint, mapping, at: Date.now() }));
    pruneOldest(store);
  } catch {
    // A full quota is not worth failing an import over.
  }
}

/** The mapping last confirmed for this exact sheet shape, if any. */
export function recallMapping(
  kind: ImportKind,
  headers: readonly string[],
): Record<string, string> | null {
  const store = storage();
  if (store === null) return null;
  try {
    const fingerprint = headerFingerprint(kind, headers);
    const raw = store.getItem(keyOf(fingerprint));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return null;
    const record = parsed as { fingerprint?: unknown; mapping?: unknown };
    // A hash collision must not apply someone else's mapping.
    if (record.fingerprint !== fingerprint) return null;
    if (record.mapping === null || typeof record.mapping !== "object") return null;
    const out: Record<string, string> = {};
    for (const [source, target] of Object.entries(record.mapping as Record<string, unknown>)) {
      if (typeof target === "string" && target.length > 0) out[source] = target;
    }
    return Object.keys(out).length > 0 ? out : null;
  } catch {
    return null;
  }
}

function pruneOldest(store: Storage): void {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key?.startsWith(PREFIX) === true) keys.push(key);
  }
  if (keys.length <= MAX_ENTRIES) return;
  const dated = keys.map((key) => {
    let at = 0;
    try {
      const parsed: unknown = JSON.parse(store.getItem(key) ?? "{}");
      const value = (parsed as { at?: unknown } | null)?.at;
      if (typeof value === "number") at = value;
    } catch {
      at = 0;
    }
    return { key, at };
  });
  dated.sort((a, b) => a.at - b.at);
  for (const entry of dated.slice(0, dated.length - MAX_ENTRIES)) store.removeItem(entry.key);
}
